import { Modal, Box, IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faEnvelope, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { navigateExternal } from '../utils/navigation.js';
import "./behaviorUpgradeModal.css";

const PLAN_HIERARCHY = [
  { name: 'Free', documentLimit: 50 },
  { name: 'Smart', documentLimit: 200 },
  { name: 'Boost', documentLimit: 1000 },
  { name: 'Ultimo', documentLimit: 10000 },
];

// Exported so the Knowledge Base usage bar can hide its Upgrade CTA when there is
// nothing left to sell (this modal shows Contact Sales in that case).
export const getBestKnowledgePlan = (currentPlanName, currentCount) => {
  const currentIndex = PLAN_HIERARCHY.findIndex(p => p.name === currentPlanName);

  for (let i = currentIndex + 1; i < PLAN_HIERARCHY.length; i++) {
    if (PLAN_HIERARCHY[i].documentLimit > currentCount) {
      return PLAN_HIERARCHY[i];
    }
  }

  return null;
};

// `description`: optional override for the body copy (e.g. "you selected N, only M left").
// `secondaryAction`: optional { label, onClick } shown under Upgrade Now (e.g. "Add the first N only").
const KnowledgeUpgradeModal = ({ open, onClose, bot, onUpgrade, currentKnowledgeCount = 0, loading = false, description = null, secondaryAction = null }) => {
  const { t } = useTranslation();

  if (!open) return null;

  const currentPlanName = bot?.subscription_plan?.name || 'Free';
  const currentLimit = bot?.subscription_plan?.document_limit ?? 10;
  const bestPlan = getBestKnowledgePlan(currentPlanName, currentKnowledgeCount);
  const isHighestTier = !bestPlan;

  const handleContactSales = () => {
    navigateExternal('mailto:build@ultimo-bots.com?subject=Enterprise%20Knowledge%20Limit%20Inquiry');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: 'none',
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          maxWidth: "500px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          outline: 'none',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
          aria-label={t('close')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
        <div className="behavior-upgrade-modal-content">
          <div className="behavior-upgrade-modal-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <h2 className="behavior-upgrade-modal-title">
            {t('knowledge_limit_reached_title')}
          </h2>

          <p className="behavior-upgrade-modal-description">
            {description
              ? description
              : isHighestTier
                ? t('knowledge_limit_highest_tier_desc')
                : t('knowledge_limit_upgrade_desc')
            }
          </p>

          {isHighestTier ? (
            <button
              className="behavior-upgrade-button behavior-upgrade-contact-button"
              onClick={handleContactSales}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {t('contact_sales')}
            </button>
          ) : (
            <>
              <div className="behavior-upgrade-plan-info">
                <div className="behavior-upgrade-plan-badge behavior-upgrade-current-plan">
                  <span className="behavior-upgrade-plan-label">{t('current_plan')}</span>
                  <span className="behavior-upgrade-plan-name">{currentPlanName}</span>
                  <span className="behavior-upgrade-plan-limit">
                    {t('knowledge_n_sources', { count: currentLimit })}
                  </span>
                </div>
                <div className="behavior-upgrade-arrow-icon">→</div>
                <div className="behavior-upgrade-plan-badge behavior-upgrade-next-plan">
                  <span className="behavior-upgrade-plan-label">{t('recommended')}</span>
                  <span className="behavior-upgrade-plan-name">{bestPlan.name}</span>
                  <span className="behavior-upgrade-plan-limit">
                    {t('knowledge_n_sources', { count: bestPlan.documentLimit })}
                  </span>
                </div>
              </div>

              <button
                className="behavior-upgrade-button"
                onClick={onUpgrade}
                disabled={loading}
              >
                {loading
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('redirecting')}</>
                  : t('upgrade_now')
                }
              </button>
              {secondaryAction && (
                <button type="button" className="behavior-upgrade-secondary" onClick={secondaryAction.onClick} disabled={loading}>
                  {secondaryAction.label}
                </button>
              )}
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default KnowledgeUpgradeModal;
